/**
 * 8D scoring criteria — the single source of truth for the four grading
 * dimensions (CR-7356).
 *
 * Dennis Eagle grade an 8D across four dimensions, each 1–5. Until now the
 * only score text lived in `CarScoreFields.tsx`, covered two dimensions at
 * levels 1/3/5 only, and was shown solely in the customer's review panel — so
 * suppliers completing the 8D never saw what "good" looks like.
 *
 * This module holds all four dimensions × five levels verbatim, feeding both
 * the supplier-facing "How this 8D is scored" panels (ScoreCriteriaPanel) and
 * the RC/CA score dropdowns. Each level is split into a short `headline` (the
 * dropdown label) and the fuller `detail` (shown in the guidance panels).
 *
 * Where the dimensions live in the 8D:
 *   Root Cause section       → ORC (Occurrence) + ERC (Escape)
 *   Corrective Action section → OPA (Occurrence) + EPA (Escape)
 */

export interface ScoreLevel {
  /** 1–5. */
  level: number;
  /** Short phrase used as the dropdown label and the panel row heading. */
  headline: string;
  /** The fuller explanation shown under the headline in the guidance panel. */
  detail: string;
}

export type ScoreDimensionKey = 'ORC' | 'ERC' | 'OPA' | 'EPA';

export interface ScoreDimension {
  key: ScoreDimensionKey;
  /** Three-letter abbreviation shown to the user, e.g. "ORC". */
  abbr: string;
  /** Full dimension name, e.g. "Occurrence Root Cause". */
  name: string;
  levels: ScoreLevel[];
}

export const SCORE_DIMENSIONS: Record<ScoreDimensionKey, ScoreDimension> = {
  ORC: {
    key: 'ORC',
    abbr: 'ORC',
    name: 'Occurrence Root Cause',
    levels: [
      { level: 1, headline: 'Symptom identified only', detail: '"The operator made a mistake." No understanding of why the defect occurred.' },
      { level: 2, headline: 'Potential cause suggested but not proven', detail: 'A likely cause has been proposed but lacks supporting evidence or validation.' },
      { level: 3, headline: 'Process failure identified but underlying cause not fully established', detail: 'Where the process broke down, but not why it was allowed to.' },
      { level: 4, headline: 'Root cause validated with objective evidence', detail: 'I understand exactly why this defect occurred and have implemented effective corrective action.' },
      { level: 5, headline: 'Root cause validated and system cause identified where applicable', detail: 'Why it occurred, why my quality system failed to prevent it, and the system improved to reduce similar failures elsewhere.' },
    ],
  },
  ERC: {
    key: 'ERC',
    abbr: 'ERC',
    name: 'Escape Root Cause',
    levels: [
      { level: 1, headline: 'No investigation into why defect reached the customer', detail: '"The inspector missed it."' },
      { level: 2, headline: 'Inspection failure acknowledged but poorly understood', detail: 'Detection failure suspected but not evidenced.' },
      { level: 3, headline: 'Detection process weakness identified', detail: 'Which control failed, but not why it was ineffective.' },
      { level: 4, headline: 'Escape mechanism validated with objective evidence', detail: 'I understand exactly why this defect escaped and have implemented effective corrective action.' },
      { level: 5, headline: 'Escape mechanism validated and system cause identified where applicable', detail: 'Why it escaped, why the quality system failed to detect it, and the system improved.' },
    ],
  },
  OPA: {
    key: 'OPA',
    abbr: 'OPA',
    name: 'Occurrence Prevention Actions',
    levels: [
      { level: 1, headline: 'Administrative action only', detail: 'Training, awareness briefings or reminders.' },
      { level: 2, headline: 'Additional checks or awareness introduced', detail: 'More supervision or additional sign-off activities.' },
      { level: 3, headline: 'Process-level improvement implemented', detail: 'Work instructions, process flow or controls improved.' },
      { level: 4, headline: 'Robust corrective action implemented targeting validated root cause', detail: 'Prevents recurrence of this specific issue.' },
      { level: 5, headline: 'System-level improvement implemented where appropriate', detail: 'Wider management system, APQP, PFMEA, Control Plan, NPI process, document control or similar improved.' },
    ],
  },
  EPA: {
    key: 'EPA',
    abbr: 'EPA',
    name: 'Escape Prevention Actions',
    levels: [
      { level: 1, headline: 'More inspection requested', detail: 'Reliance on people paying more attention.' },
      { level: 2, headline: 'Additional inspection activity added', detail: 'Increased inspection frequency or checks.' },
      { level: 3, headline: 'Improved detection method introduced', detail: 'More effective inspection or verification process.' },
      { level: 4, headline: 'Robust detection control implemented', detail: 'Prevents recurrence of this specific escape route.' },
      { level: 5, headline: 'System-level detection improvement implemented where appropriate', detail: 'Reduces similar escapes across other products, processes or customers.' },
    ],
  },
};

/** The Root Cause section's dimensions (occurrence + escape). */
export const ROOT_CAUSE_DIMENSIONS: ScoreDimension[] = [
  SCORE_DIMENSIONS.ORC,
  SCORE_DIMENSIONS.ERC,
];

/** The Corrective Action section's dimensions (occurrence + escape). */
export const CORRECTIVE_ACTION_DIMENSIONS: ScoreDimension[] = [
  SCORE_DIMENSIONS.OPA,
  SCORE_DIMENSIONS.EPA,
];

/**
 * The Escape half of the scoring model. ERC and EPA grade the Escape root
 * cause and Escape prevention actions — sections that only exist while the
 * NCR Escape chain is on for the customer (Admin Setup → Quality,
 * `ncr_escape_root_cause_enabled`). With the chain off there is nothing for
 * them to grade, so the surfaces that show criteria are hidden wholesale by
 * the same per-customer switch rather than filtering dimension by dimension.
 */
export const ESCAPE_DIMENSION_KEYS = ['ERC', 'EPA'] as const;


/**
 * Build 1–5 dropdown options from a dimension: value is the integer, label is
 * "<n> — <headline>". Keeps the score dropdowns and the guidance panels reading
 * off the same definitions.
 */
export function scoreOptions(
  dimension: ScoreDimension
): { value: string; label: string }[] {
  return dimension.levels.map((l) => ({
    value: String(l.level),
    label: `${l.level} — ${l.headline}`,
  }));
}
